import React, { useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {Plus, Trash2, X} from "lucide-react";
import { cn } from "@/lib/utils";

interface Column {
    key: string;
    label: string;
    span: number;
    align?: 'left' | 'center' | 'right';
}

interface FormField {
    id: string;
    [key: string]: any;
}

type RowSize = "compact" | "normal" | "relaxed";

interface FormTableProps {
    title: string;
    columns: Column[];
    fields: FormField[];
    watched?: Record<string, any>[];
    errors?: Record<string, any>;
    renderCell: (field: FormField, columnKey: string, index: number) => React.ReactNode;
    onAddRow: () => void;
    onRemoveRow: (index: number) => void;
    canRemoveRow?: (index: number) => boolean;
    addButtonText?: string;
    showRowNumbers?: boolean;
    autoAddRow?: boolean;
    primaryField?: string;
    rowSize?: RowSize;
}

const ROW_PADDING: Record<RowSize, string> = {
    compact:  "py-1",
    normal:   "py-1.5",
    relaxed:  "py-2",
};

export default function FormTable({
                                      title,
                                      columns,
                                      fields,
                                      watched = [],
                                      errors,
                                      renderCell,
                                      onAddRow,
                                      onRemoveRow,
                                      canRemoveRow = () => true,
                                      addButtonText = "Add Row",
                                      showRowNumbers = true,
                                      autoAddRow = true,
                                      primaryField,
                                      rowSize = "normal",
                                  }: FormTableProps) {
    const rowPy = ROW_PADDING[rowSize];

    const shouldAutoAdd = useMemo(() => {
        if (!autoAddRow || !primaryField || !watched || watched.length === 0 || fields.length >= 3) {
            return false;
        }
        const lastRow = watched[watched.length - 1];
        const hasPrimary   = !!lastRow?.[primaryField]?.trim();
        const hasSecondary = !!(lastRow?.dosage?.trim() || lastRow?.frequency?.trim() || lastRow?.complaint?.trim());
        return (
            watched.length === fields.length &&
            hasPrimary &&
            hasSecondary &&
            watched.length <= 2
        );
    }, [watched, autoAddRow, primaryField, fields.length]);

    useEffect(() => {
        if (shouldAutoAdd) onAddRow();
    }, [shouldAutoAdd, onAddRow]);

    return (
        <div className="w-full space-y-1">
            <div className="w-full overflow-hidden rounded-md border border-border bg-card shadow-sm">
                <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[640px] table-fixed border-collapse text-left">
                    {/* Header */}
                    <thead>
                    <tr className="bg-muted/50 border-b border-border/40 select-none bg-muted">
                        {showRowNumbers && (
                            <th className="w-10 text-center text-[11px] font-bold text-muted-foreground uppercase tracking-wider py-2.5 px-1 border-r border-border/25 bg-muted">
                                #
                            </th>
                        )}

                        {columns.map((col) => {
                            const widthPct = `${(col.span / 12) * 100}%`;
                            return (
                                <th
                                    key={col.key}
                                    style={{ width: widthPct }}
                                    className={cn(
                                        "px-3 py-2.5 text-[11px] font-bold text-muted-foreground uppercase tracking-wider bg-muted ",
                                        col.align === 'center' && 'text-center',
                                        col.align === 'right'  && 'text-right',
                                    )}
                                >
                                    {col.label}
                                </th>
                            );
                        })}

                        {/* Actions Column */}
                        <th className="w-10 p-0 bg-muted" />
                    </tr>
                    </thead>

                    {/* Rows */}
                    <tbody className="divide-y divide-border/20">
                    {fields.map((field, index) => (
                        <tr
                            key={field.id}
                            className="group"
                        >
                            {/* Row number */}
                            {showRowNumbers && (
                                <td className={cn("text-center align-top px-1 border-r border-border/25 bg-muted/5 select-none", rowPy)}>
                                        <span className="text-[11px] text-muted-foreground/70 font-semibold tabular-nums">
                                            {index + 1}
                                        </span>
                                </td>
                            )}

                            {/* Cells */}
                            {columns.map((col) => (
                                <td
                                    key={col.key}
                                    /* CRITICAL UX FIX: Removed cell padding (p-0) and used items-stretch.
                                      Your inputs will now touch the edges of the cell grid perfectly.
                                    */
                                    className={cn(
                                        "p-0 align-top border-r border-border/25 last:border-r-0 min-w-0",
                                        "transition-colors duration-100"
                                    )}
                                >
                                    <div className={cn("w-full min-w-0 flex flex-col justify-start", rowPy)}>
                                        {renderCell(field, col.key, index)}
                                    </div>
                                </td>
                            ))}

                            {/* Remove button */}
                            <td className={cn("p-0 text-center", rowPy)}>
                                <div className="flex items-center justify-center w-full h-full">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0 text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 opacity-40 group-hover:opacity-100 transition-all duration-100 rounded"
                                        onClick={() => onRemoveRow(index)}
                                        disabled={!canRemoveRow(index)}
                                        title={`Remove ${title.toLowerCase()}`}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                </div>

                {/* Footer Action */}
                <div className="border-t border-border/25 bg-muted/70 px-3 py-1 ">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onAddRow}
                        className="text-primary hover:text-primary hover:bg-primary/10 text-xs font-semibold h-8 px-2.5 rounded transition-all duration-100 "
                    >
                        <Plus className="w-3 h-3 mr-1" />
                        {addButtonText}
                    </Button>
                </div>
            </div>
        </div>
    );
}

// Column configs preserved intact
export const MedicineTableColumns: Column[] = [
    { key: 'medicine',   label: 'Medicine',  span: 4 },
    { key: 'dosage',     label: 'Dosage',    span: 2 },
    { key: 'taken_when', label: 'When',      span: 2 },
    { key: 'route',      label: 'Route',     span: 1 },
    { key: 'duration',   label: 'Duration',  span: 1 },
    { key: 'quantity',   label: 'Qty',       span: 1, align: 'center' },
    { key: 'notes',      label: 'Notes',     span: 1 },
];

export const ComplaintsTableColumns: Column[] = [
    { key: 'complaint',  label: 'Complaint',  span: 4 },
    { key: 'frequency',  label: 'Frequency',  span: 2 },
    { key: 'severity',   label: 'Severity',   span: 2 },
    { key: 'duration',   label: 'Duration',   span: 2 },
    { key: 'date',       label: 'Since',      span: 2 },
];